/**
 * LA PANTALLA CLIENTES, COMO EN EL LIENZO «D · La Calma con nuestro diseño» (tarea 10 de la
 * hoja del 15-10; diseño del 19-10). Desde el 19-10 sustituye a la lista vieja de Clientes
 * (Francisco: «este rediseño reemplaza a la pestaña de clientes que ya existía»).
 *
 * Cada fila: la fecha de su último reporte MENSUAL en naranja y, si después se le guardaron
 * macros, «macros» en verde; si no, «cambiar macros» en rojo. La decisión (verde, rojo o
 * nada) la toma el servidor (`core/macros_tras_el_mensual`); aquí solo se ordena y se escribe.
 * Qué filas entran (cartera, acceso, momento) lo decide `lib/filtrosDeClientes`.
 *
 * El orden de siempre («Reporte mensual»): arriba los que esperan macros, el que lleva más
 * esperando primero; luego los que ya los tienen, del mensual más reciente al más antiguo;
 * al final los que no han mandado mensual.
 *
 * Desde el 20-10 (vídeo de Jesús) los de «cambiar macros» van SIEMPRE arriba, se ordene por
 * lo que se ordene; dentro de cada grupo (los que esperan y el resto), el orden elegido.
 */
import { diasDesdeHoyLocal, pintarDia } from './horaEspana';
import { estaFuera } from './cuentaClientes';
import { semanaDeRutinaDe } from './filtrosDeClientes';
import { estadoDeAcceso, nombreDePlan } from './labels';
import { euros } from './precios';
import { contiene } from './sinTildes';

export const ESTADO_MACROS = { HECHOS: 'hechos', CAMBIAR: 'cambiar' };
/** La baja pedida de la que no consta el día (Gestión de suscripciones). */
export const SIN_FECHA = 'Sí, sin fecha';

const CORTA = { day: 'numeric', month: 'short', year: 'numeric' };
const SIN_ANO = { day: 'numeric', month: 'short' };
const LARGA = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };

const GRUPO = { [ESTADO_MACROS.CAMBIAR]: 0, [ESTADO_MACROS.HECHOS]: 1 };
const grupo = (c) => GRUPO[c.estado_macros] ?? 2;
const esperaMacros = (c) => c.estado_macros === ESTADO_MACROS.CAMBIAR;
// Los que esperan macros, primero; luego, el orden elegido.
const primeroLosQueEsperan = (comparar) => (a, b) =>
    (Number(esperaMacros(b)) - Number(esperaMacros(a))) || comparar(a, b);
const nombre = (c) => c.user?.name || c.user?.email || '';
const porNombre = (a, b) => nombre(a).localeCompare(nombre(b), 'es');
// Las fechas vacías al final, sea cual sea el orden.
const porFecha = (campo) => (a, b) => {
    const fa = a[campo] ? String(a[campo]).slice(0, 10) : '';
    const fb = b[campo] ? String(b[campo]).slice(0, 10) : '';
    if (fa === fb) return porNombre(a, b);
    if (!fa) return 1;
    if (!fb) return -1;
    return fa < fb ? -1 : 1;
};

const porMensual = (a, b) => {
    const g = grupo(a) - grupo(b);
    if (g) return g;
    const fa = a.ultimo_mensual || '';
    const fb = b.ultimo_mensual || '';
    if (fa !== fb) {
        // El que espera macros desde hace más, primero; en verde, el mensual más reciente.
        return grupo(a) === 0 ? (fa < fb ? -1 : 1) : (fa > fb ? -1 : 1);
    }
    return porNombre(a, b);
};

const diasSinTocar = (c) => (c.ultimo_ajuste ? Math.max(0, diasDesdeHoyLocal(String(c.ultimo_ajuste).slice(0, 10))) : null);

/** «Ordenar por» del lienzo. `valor` es lo que sale en la pastilla naranja cuando se ordena
 *  por otra cosa que el mensual. «Sin tocar» es el orden de la lista vieja al que lleva el
 *  Dashboard (`?orden=sin_tocar`, punto 29): el que nunca ha tenido macros, arriba. */
export const ORDENES = [
    ['mensual', 'Reporte mensual', porMensual, null],
    ['nombre', 'Nombre', porNombre, null],
    ['email', 'Correo', (a, b) => (a.user?.email || '').localeCompare(b.user?.email || '', 'es'), null],
    ['fin', 'Fin de membresía', porFecha('renueva_el'),
        (c) => (c.comp_plan ? 'Sin fin' : c.renueva_el ? `Fin ${pintarDia(c.renueva_el, SIN_ANO)}` : 'Sin fin')],
    ['pago', 'Próximo pago', porFecha('proximo_cobro'),
        (c) => (c.proximo_cobro ? `Pago ${pintarDia(c.proximo_cobro, SIN_ANO)}` : 'Sin cobro')],
    ['semana', 'Semana', (a, b) => ((a.week ?? 999) - (b.week ?? 999)) || porNombre(a, b),
        (c) => (c.week != null ? `Semana ${c.week}` : 'Sin semana')],
    ['sin_tocar', 'Sin tocar macros', (a, b) => {
        const da = diasSinTocar(a), db = diasSinTocar(b);
        if (da === db) return porNombre(a, b);
        if (da === null) return -1;
        if (db === null) return 1;
        return db - da;
    }, (c) => { const d = diasSinTocar(c); return d === null ? 'Sin macros' : `${d} ${d === 1 ? 'día' : 'días'}`; }],
];

// EL ORDEN POR COLUMNA (21-10): las flechas de cada título de la tabla. `asc` es de menor a
// mayor (A→Z, la fecha más antigua, la semana más baja) y `desc` al revés. Los que no tienen
// el dato van siempre al final, en los dos sentidos, y a igualdad manda el nombre.
// Cada entrada: [clave, cómo se saca el dato, ¿es un número?]. `catalogo` es para el plan y
// `entrenadores` (la lista del equipo, `/admin/trainers`) para el nombre del entrenador.
const COLUMNAS_QUE_ORDENAN = [
    // ACCEDER A CLIENTE: con ficha delante de «Sin ficha».
    ['ficha', (c) => (c.id ? 0 : 1), true],
    ['reporte', (c) => (c.ultimo_mensual ? String(c.ultimo_mensual).slice(0, 10) : null), false],
    // MACROS: «cambiar macros», luego «macros», y al final los que no tienen ninguno.
    ['macros', (c) => GRUPO[c.estado_macros] ?? null, true],
    ['nombre', (c) => nombre(c) || null, false],
    ['email', (c) => c.user?.email || null, false],
    ['plan', (c, catalogo) => (c.plan ? nombreDePlan(c.plan, catalogo) : null), false],
    // ENTRENADOR (hoja del 22-10, fila 75): por su nombre; los que no tienen, al final.
    ['entrenador', (c, catalogo, entrenadores) => nombreDeEntrenador(entrenadores, c.trainer_id), false],
    ['ciclo', (c) => c.week ?? null, true],
    ['rutina', (c) => semanaDeRutinaDe(c)?.n ?? null, true],
    // REPITE SEMANA (21-10): las semanas que lleva sin mandar el mensual (`core/repite_semana`).
    ['repite', (c) => c.repite_semanas ?? null, true],
    // GESTIÓN DE SUSCRIPCIONES (21-10, `core/suscripciones`): los pagos por su día y la baja
    // por el instante en que la pidió. La baja sin fecha (vino de Calma o de Stripe) va
    // detrás de las fechadas y delante de los que no la han pedido.
    ['primer_pago', (c) => (c.primer_pago ? String(c.primer_pago).slice(0, 10) : null), false],
    ['ultimo_pago', (c) => (c.ultimo_pago ? String(c.ultimo_pago).slice(0, 10) : null), false],
    ['baja', (c) => (c.baja_pedida_el ? String(c.baja_pedida_el) : c.baja_sin_fecha ? '9999' : null), false],
];
export const SENTIDOS = ['asc', 'desc'];

const porColumna = ([, dato, esNumero], sentido, catalogo, entrenadores) => (a, b) => {
    const va = dato(a, catalogo, entrenadores);
    const vb = dato(b, catalogo, entrenadores);
    if (va === vb) return porNombre(a, b);
    if (va === null || va === undefined) return 1;
    if (vb === null || vb === undefined) return -1;
    const cmp = esNumero ? va - vb : String(va).localeCompare(String(vb), 'es');
    return (sentido === 'desc' ? -cmp : cmp) || porNombre(a, b);
};

export const CLAVES_DE_ORDEN = [...new Set([
    ...ORDENES.map(([clave]) => clave), ...COLUMNAS_QUE_ORDENAN.map(([clave]) => clave)])];
const elOrden = (clave) => ORDENES.find(([k]) => k === clave) || ORDENES[0];

/** Cómo se compara con este orden. Una columna de la tabla usa su flecha (`sentido`); los
 *  órdenes de antes («Reporte mensual», el de siempre, y `?orden=sin_tocar`) van como iban. */
const comparador = (orden, sentido, catalogo, entrenadores) => {
    const columna = COLUMNAS_QUE_ORDENAN.find(([k]) => k === orden);
    return columna ? porColumna(columna, sentido, catalogo, entrenadores) : elOrden(orden)[2];
};

/** El nombre del entrenador de un cliente en la lista del equipo; null si no tiene o si no
 *  sale en la lista (borrado, o la lista no llegó). */
export const nombreDeEntrenador = (entrenadores, id) =>
    (id ? (entrenadores || []).find((t) => t.id === id)?.name || null : null);

/** Las pastillas de plan: «Todos» y un plan por cada uno que haya en la lista, el que más
 *  clientes tiene primero. Del catálogo y de los datos, no cableados (punto 40). */
export const pastillasDePlan = (clientes, catalogo) => {
    const cuenta = new Map();
    (clientes || []).forEach((c) => { if (c?.plan) cuenta.set(c.plan, (cuenta.get(c.plan) || 0) + 1); });
    return [['all', 'Todos', (clientes || []).length],
        ...[...cuenta.entries()]
            .sort((a, b) => b[1] - a[1] || String(a[0]).localeCompare(String(b[0])))
            .map(([codigo, n]) => [codigo, nombreDePlan(codigo, catalogo), n])];
};

// LAS CELDAS DEL SEMÁFORO (punto 32), en el orden de la lista vieja. El color y el texto
// los manda el servidor; aquí solo se nombran.
export const CELDAS_DEL_SEMAFORO = [
    ['ajuste', 'Sin tocar macros'], ['reporte', 'Último reporte'], ['contacto', 'Contacto'],
    ['peso', 'Peso'], ['perfil_largo', 'Perfil'],
];

// RENUEVA (punto 45): sola con Stripe detrás, «según el plan» cuando lo dice el catálogo pero
// no hay suscripción viva, y a mano el resto. Son tres cosas distintas.
const renueva = (c) => {
    const r = c.renueva_solo || {};
    if (r.automatica && r.via === 'stripe') return { texto: 'Sola', tono: 'ok', ayuda: 'Stripe le cobra solo' };
    if (r.automatica) return { texto: 'Según el plan', tono: 'aviso', ayuda: 'Su plan renueva automático, pero no hay suscripción viva en Stripe' };
    return { texto: 'A mano', tono: 'apagado', ayuda: 'No se le cobra solo: hay que pedirle la renovación' };
};

// LO QUE PAGA y lo que deja al mes (doc 19-08), con de dónde sale el «al mes» (punto 43).
const precio = (c) => {
    if (c.price == null) return null;
    const alMes = c.precio_mensual != null && c.precio_mensual !== c.price
        ? ` (${euros(c.precio_mensual)} al mes${c.precio_fuente === 'cobro_real' ? ', por su último cobro' : ''})`
        : '';
    return `${euros(c.price)}${alMes}`;
};

// DÍAS SIN CERRAR (doc «El día», 31-08): al que lo apagó no se le cuentan; la cuenta del
// servidor se corta a los 60, así que ahí se dice «más de 60».
const sinCerrar = (c) => {
    if (c.cierre_apagado) return { texto: 'lo tiene apagado', alerta: false };
    if (!(c.dias_sin_cerrar > 0)) return null;
    const n = c.dias_sin_cerrar;
    return { texto: n >= 60 ? 'más de 60 días' : `${n} ${n === 1 ? 'día' : 'días'}`, alerta: n >= 4 };
};

// LO QUE DEBE (19-08): semanas sin reporte y reportes que le tocaban y no mandó.
const debe = (c) => [
    c.semanas_sin_reporte > 0 && `${c.semanas_sin_reporte} ${c.semanas_sin_reporte === 1 ? 'semana' : 'semanas'} sin reporte`,
    c.reportes_sin_responder > 0 && `debe ${c.reportes_sin_responder} ${c.reportes_sin_responder === 1 ? 'reporte' : 'reportes'}`,
].filter(Boolean).join(' · ') || null;

const rutina = (c) => {
    const s = c.semana_rutina == null ? null : semanaDeRutinaDe(c);
    if (!s) return 'sin rutina';
    return `semana ${s.n}${s.largo ? ` de ${s.largo}` : ''} de rutina`;
};

// LAS COLUMNAS «SEMANA DE CICLO» Y «SEMANA DE RUTINA» (21-10): el número a secas, con su
// total cuando se sabe («3 de 12»). La de ciclo es la que empieza de nuevo con cada pago
// (`core/cycle`); la de rutina, la del bloque de entreno (`core/semana_rutina`). La de
// rutina se topa en el largo del bloque, igual que en el acordeón. Con reloj, la de rutina es
// LA DEL RELOJ (`semana_reloj`, 21-10): esa se para en la semana del mensual mientras no lo
// manda; la de los lunes desde que arrancó la rutina seguía corriendo y daba «4 de 4» a quien
// estaba en la 3 esperándolo.
const semanaDeCiclo = (c) => {
    if (c.week == null) return null;
    return c.cycle_total_weeks ? `${c.week} de ${c.cycle_total_weeks}` : String(c.week);
};
const semanaDeRutina = (c) => {
    const s = semanaDeRutinaDe(c);
    if (!s) return null;
    return s.largo ? `${s.n} de ${s.largo}` : String(s.n);
};

/** Lo que dice el acordeón de cada cliente. `hoy` es 'AAAA-MM-DD' (el del navegador).
 *  Desde el 19-10 lleva también lo que enseñaban las columnas de la lista vieja. */
const detalle = (c, hoy) => {
    const total = c.cycle_total_weeks ? ` de ${c.cycle_total_weeks}` : '';
    // Los macros puestos tras el mensual (pueden entrar en vigor más adelante) y, si no hay,
    // los vigentes. Una vigencia por delante que no venga del servidor como puesta tras el
    // mensual es un año mal puesto de la Calma (hay uno de 2029): no se enseña.
    const vigente = c.ultimo_ajuste && (!hoy || String(c.ultimo_ajuste).slice(0, 10) <= hoy)
        ? c.ultimo_ajuste : null;
    const macros = c.macros_guardados || vigente;
    const porLlegar = macros && hoy && String(macros).slice(0, 10) > hoy;
    return {
        semana: c.week != null ? `Semana ${c.week}${total}` : 'Sin semana',
        empezo: pintarDia(c.cycle_start || c.created_at, LARGA, 'sin fecha'),
        ultimoMacro: macros
            ? `${pintarDia(macros, LARGA)}${porLlegar ? ' (entra en vigor ese día)' : ''}`
            : 'sin macros guardados',
        mensualLargo: c.ultimo_mensual ? pintarDia(c.ultimo_mensual, LARGA) : 'todavía no lo ha mandado',
        proximoPago: c.proximo_cobro ? pintarDia(c.proximo_cobro, LARGA) : 'no se le cobra solo',
        hasta: c.comp_plan ? 'sin fin (cortesía)' : pintarDia(c.renueva_el, LARGA, 'sin fecha de fin'),
        planVencido: !!c.plan_vencido,
        rutina: rutina(c),
        // El estado con palabras, el mismo que ve el cliente al entrar (#64).
        acceso: estadoDeAcceso(c),
        enPausa: !!c.pausa?.desde,
        // Punto 39: sin esta marca, para saber quién tiene una excepción habría que entrar
        // en todas las fichas.
        excepcion: c.excepcion || null,
        renueva: renueva(c),
        precio: precio(c),
        sinCerrar: sinCerrar(c),
        debe: debe(c),
        semaforo: c.semaforo || {},
        ultimaEntrada: pintarDia(c.ultima_entrada, LARGA, 'no consta'),
    };
};

/** Las filas: las que llegan (ya filtradas por cartera, acceso y momento), menos tu propia
 *  ficha, buscadas por nombre o correo sin mirar tildes, del plan elegido y ordenadas.
 *  Un registro a medias no tiene ficha (`id` vacío): sale, pero sin enlaces. */
// `primeroLosQueEsperan`: los de «cambiar macros» arriba (20-10). La pestaña Gestión de
// suscripciones lo apaga: allí el orden es solo el de su columna (21-10).
export const filasDelGestor = (clientes, { buscado = '', catalogo, hoy, plan = 'all', orden = 'mensual', sentido,
    entrenadores = [], primeroLosQueEsperan: arribaLosQueEsperan = true } = {}) => {
    const [, , , valor] = elOrden(orden);
    const comparar = comparador(orden, sentido, catalogo, entrenadores);
    return (clientes || [])
        .filter((c) => c && !c.es_tu_ficha && (plan === 'all' || c.plan === plan))
        .filter((c) => contiene(`${c.user?.name || ''} ${c.user?.email || ''}`, buscado))
        .sort(arribaLosQueEsperan ? primeroLosQueEsperan(comparar) : comparar)
        .map((c) => ({
            id: c.id || null,
            clave: c.id || c.user_id || c.user?.email,
            nombre: c.user?.name || c.user?.email || 'Sin nombre',
            email: c.user?.email || '',
            plan: c.plan ? nombreDePlan(c.plan, catalogo) : 'Sin plan',
            entrenadorId: c.trainer_id || null,
            entrenador: nombreDeEntrenador(entrenadores, c.trainer_id),
            // Pidió la baja: el lienzo pinta su nombre en rojo. El mismo criterio que la columna
            // SOLICITUD DE BAJA (`core/suscripciones`): con día o sin él (21-10).
            seVa: !!(c.baja_pedida_el || c.baja_sin_fecha || c.cancel_at_period_end),
            // Ya no está (solo sale al buscarlo, `filtrosDeClientes.clientesDeLaLista`).
            fuera: estaFuera(c),
            mensual: c.ultimo_mensual ? pintarDia(c.ultimo_mensual, CORTA) : null,
            valorOrden: valor ? valor(c) : null,
            estado: c.estado_macros || null,
            semanaCiclo: semanaDeCiclo(c),
            semanaRutina: semanaDeRutina(c),
            primerPago: c.primer_pago ? pintarDia(c.primer_pago, CORTA) : null,
            ultimoPago: c.ultimo_pago ? pintarDia(c.ultimo_pago, CORTA) : null,
            bajaPedida: c.baja_pedida_el ? pintarDia(c.baja_pedida_el, CORTA) : c.baja_sin_fecha ? SIN_FECHA : null,
            repite: c.repite_semanas
                ? `${c.repite_semanas} ${c.repite_semanas === 1 ? 'semana' : 'semanas'}` : null,
            ...detalle(c, hoy),
        }));
};

/** Las filas con las que se puede actuar en grupo (marcar, desactivar, asignar tarea): con
 *  ficha y activas. Las «Fuera» solo salen al buscarlas, para abrir su ficha (20-10). */
export const sePuedeMarcar = (f) => !!f?.id && !f.fuera;

/** «Número de miembros»: los que salen y siguen dentro, busque o no (20-10). */
export const cuantosMiembros = (filas) => (filas || []).filter((f) => !f.fuera).length;

/** Cuántos esperan macros (el número de la cabecera). */
export const cuantosEsperanMacros = (filas) =>
    (filas || []).filter((f) => !f.fuera && f.estado === ESTADO_MACROS.CAMBIAR).length;

/** A dónde lleva cada pastilla: la ficha, su Seguimiento o sus Macros (`?pestana=`, que
 *  la ficha ya sabe abrir: `lib/pantallasDelPanel.pestanaDeEntrada`). */
export const enlaceDeLaFicha = (id, pestana) =>
    `/admin/clients/${id}${pestana ? `?pestana=${pestana}` : ''}`;
